import { useEffect, useState } from "react";
import { createCustomRegistrationField, updateCustomRegistrationField } from "../../api/customRegistrationFields";
import {
  CustomRegistrationFieldDTO,
  CreateCustomRegistrationFieldDTO,
  UpdateCustomRegistrationFieldDTO,
  FieldType,
  FieldTypeLabels,
} from "../../models/ICustomRegistrationField";
import { Alert, Button, Checkbox, Dialog, Field, FormGrid, Input, Select, Textarea } from "../../ui";
import "./styles.scss";

type CustomRegistrationFieldDialogProps = {
  tournamentCategoryMappingId: string;
  field?: CustomRegistrationFieldDTO;
  isOpen: boolean;
  onClose: (update?: boolean) => void;
};

const CustomRegistrationFieldDialog = (props: CustomRegistrationFieldDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    fieldName: "",
    fieldKey: "",
    fieldType: "string" as FieldType,
    isRequired: false,
    isEncrypted: false,
    placeholder: "",
    helpText: "",
    displayOrder: 0,
    minLength: "",
    maxLength: "",
    minValue: "",
    maxValue: "",
    validationPattern: "",
    validationMessage: "",
    isActive: true,
  });

  useEffect(() => {
    if (props.isOpen) {
      if (props.field) {
        setFormData({
          fieldName: props.field.fieldName,
          fieldKey: props.field.fieldKey,
          fieldType: props.field.fieldType,
          isRequired: props.field.isRequired,
          isEncrypted: props.field.isEncrypted,
          placeholder: props.field.placeholder || "",
          helpText: props.field.helpText || "",
          displayOrder: props.field.displayOrder,
          minLength: props.field.minLength?.toString() || "",
          maxLength: props.field.maxLength?.toString() || "",
          minValue: props.field.minValue?.toString() || "",
          maxValue: props.field.maxValue?.toString() || "",
          validationPattern: props.field.validationPattern || "",
          validationMessage: props.field.validationMessage || "",
          isActive: props.field.isActive,
        });
      } else {
        setFormData({
          fieldName: "",
          fieldKey: "",
          fieldType: "string",
          isRequired: false,
          isEncrypted: false,
          placeholder: "",
          helpText: "",
          displayOrder: 0,
          minLength: "",
          maxLength: "",
          minValue: "",
          maxValue: "",
          validationPattern: "",
          validationMessage: "",
          isActive: true,
        });
      }
    }
  }, [props.isOpen, props.field]);

  const handleClose = (update?: boolean) => {
    props.onClose(update);
    setErrorMessage("");
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const generateFieldKey = (fieldName: string) => {
    return fieldName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .trim();
  };

  const handleFieldNameChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      fieldName: value,
      fieldKey: !props.field ? generateFieldKey(value) : prev.fieldKey,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.fieldName.trim()) {
      setErrorMessage("Field name is required");
      return;
    }

    if (!formData.fieldKey.trim()) {
      setErrorMessage("Field key is required");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      if (props.field) {
        // Update existing field
        const updateData: UpdateCustomRegistrationFieldDTO = {
          fieldName: formData.fieldName,
          fieldType: formData.fieldType,
          isRequired: formData.isRequired,
          isEncrypted: formData.isEncrypted,
          placeholder: formData.placeholder || undefined,
          helpText: formData.helpText || undefined,
          displayOrder: formData.displayOrder,
          minLength: formData.minLength ? parseInt(formData.minLength) : undefined,
          maxLength: formData.maxLength ? parseInt(formData.maxLength) : undefined,
          minValue: formData.minValue ? parseFloat(formData.minValue) : undefined,
          maxValue: formData.maxValue ? parseFloat(formData.maxValue) : undefined,
          validationPattern: formData.validationPattern || undefined,
          validationMessage: formData.validationMessage || undefined,
          isActive: formData.isActive,
        };
        await updateCustomRegistrationField(props.field.id, updateData);
      } else {
        // Create new field
        const createData: CreateCustomRegistrationFieldDTO = {
          fieldName: formData.fieldName,
          fieldKey: formData.fieldKey,
          fieldType: formData.fieldType,
          isRequired: formData.isRequired,
          isEncrypted: formData.isEncrypted,
          placeholder: formData.placeholder || undefined,
          helpText: formData.helpText || undefined,
          displayOrder: formData.displayOrder,
          minLength: formData.minLength ? parseInt(formData.minLength) : undefined,
          maxLength: formData.maxLength ? parseInt(formData.maxLength) : undefined,
          minValue: formData.minValue ? parseFloat(formData.minValue) : undefined,
          maxValue: formData.maxValue ? parseFloat(formData.maxValue) : undefined,
          validationPattern: formData.validationPattern || undefined,
          validationMessage: formData.validationMessage || undefined,
        };
        await createCustomRegistrationField(props.tournamentCategoryMappingId, createData);
      }
      handleClose(true);
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const renderValidationFields = () => {
    switch (formData.fieldType) {
      case "string":
      case "textarea":
      case "email":
      case "phone":
        return (
          <FormGrid columns={2}>
            <Field label="Min Length">
              {(id) => (
                <Input
                  id={id}
                  type="number"
                  min="0"
                  placeholder="0"
                  value={formData.minLength}
                  onChange={(e) => handleChange("minLength", e.target.value)}
                />
              )}
            </Field>
            <Field label="Max Length">
              {(id) => (
                <Input
                  id={id}
                  type="number"
                  min="0"
                  placeholder="Unlimited"
                  value={formData.maxLength}
                  onChange={(e) => handleChange("maxLength", e.target.value)}
                />
              )}
            </Field>
          </FormGrid>
        );

      case "number":
        return (
          <FormGrid columns={2}>
            <Field label="Min Value">
              {(id) => (
                <Input
                  id={id}
                  type="number"
                  placeholder="No minimum"
                  value={formData.minValue}
                  onChange={(e) => handleChange("minValue", e.target.value)}
                />
              )}
            </Field>
            <Field label="Max Value">
              {(id) => (
                <Input
                  id={id}
                  type="number"
                  placeholder="No maximum"
                  value={formData.maxValue}
                  onChange={(e) => handleChange("maxValue", e.target.value)}
                />
              )}
            </Field>
          </FormGrid>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => handleClose()}
      title={props.field ? "Edit Custom Field" : "Add Custom Field"}
      size="lg"
      busy={loading}
      className="custom-field-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={() => handleClose()} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={loading}>
            {props.field ? "Update" : "Create"}
          </Button>
        </>
      }
    >
      <div className="custom-field-dialog__body">
        {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}

        <FormGrid columns={2}>
          <Field label="Field Name" required>
            {(id) => (
              <Input
                id={id}
                type="text"
                placeholder="e.g., Passport Number"
                value={formData.fieldName}
                onChange={(e) => handleFieldNameChange(e.target.value)}
              />
            )}
          </Field>
          <Field label="Field Key" required hint="Auto-generated from field name. Cannot be changed after creation.">
            {(id) => (
              <Input
                id={id}
                type="text"
                placeholder="e.g., passport_number"
                value={formData.fieldKey}
                onChange={(e) => handleChange("fieldKey", e.target.value)}
                disabled={!!props.field}
              />
            )}
          </Field>
        </FormGrid>

        <FormGrid columns={2}>
          <Field label="Field Type" required>
            {(id) => (
              <Select id={id} block value={formData.fieldType} onChange={(e) => handleChange("fieldType", e.target.value)}>
                {(Object.entries(FieldTypeLabels) as [FieldType, string][]).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Display Order">
            {(id) => (
              <Input
                id={id}
                type="number"
                min="0"
                value={formData.displayOrder}
                onChange={(e) => handleChange("displayOrder", Number(e.target.value))}
              />
            )}
          </Field>
        </FormGrid>

        <Field label="Placeholder">
          {(id) => (
            <Input
              id={id}
              type="text"
              placeholder="Placeholder text shown in the input field"
              value={formData.placeholder}
              onChange={(e) => handleChange("placeholder", e.target.value)}
            />
          )}
        </Field>

        <Field label="Help Text">
          {(id) => (
            <Textarea
              id={id}
              placeholder="Additional instructions or guidance for the user"
              value={formData.helpText}
              onChange={(e) => handleChange("helpText", e.target.value)}
              rows={2}
            />
          )}
        </Field>

        {renderValidationFields()}

        {formData.fieldType !== "date" && formData.fieldType !== "number" && (
          <>
            <Field label="Validation Pattern (Regex)">
              {(id) => (
                <Input
                  id={id}
                  type="text"
                  placeholder="e.g., ^[A-Z]{2}[0-9]{6}$"
                  value={formData.validationPattern}
                  onChange={(e) => handleChange("validationPattern", e.target.value)}
                />
              )}
            </Field>

            <Field label="Validation Error Message">
              {(id) => (
                <Input
                  id={id}
                  type="text"
                  placeholder="e.g., Please enter a valid passport number"
                  value={formData.validationMessage}
                  onChange={(e) => handleChange("validationMessage", e.target.value)}
                />
              )}
            </Field>
          </>
        )}

        <div className="custom-field-dialog__checks">
          <Checkbox
            checked={formData.isRequired}
            onChange={(checked) => handleChange("isRequired", checked)}
            label="Required Field"
            description="Players must fill this field to complete registration"
          />

          <Checkbox
            checked={formData.isEncrypted}
            onChange={(checked) => handleChange("isEncrypted", checked)}
            label="Encrypt Data"
            description="For sensitive data like passport numbers"
          />

          {props.field && (
            <Checkbox
              checked={formData.isActive}
              onChange={(checked) => handleChange("isActive", checked)}
              label="Active"
              description="Inactive fields won't appear in registration forms"
            />
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default CustomRegistrationFieldDialog;
